import React, { useCallback } from 'react';

import type {
  AutocompleteProps,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
} from '@mui/material';
import { Autocomplete, Typography } from '@mui/material';
import { createFilterOptions } from '@mui/material/useAutocomplete';
import { useAsync } from 'react-use';
import { AlineAutocompleteInput, ContractTypeChip } from 'src/components';
import { useUniqueFilterOptions } from 'src/components/generic/data-grid/autocomplete-filter-utils';
import { CrossIcon } from 'src/components/icons/CrossIcon';
import type { Apollo } from 'src/gql/generated';
import { doNothing } from 'src/helpers';
import { useGetMyContractTypes } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

export type ExistingContractType = Pick<Apollo.ContractType, 'id' | '__typename' | 'value'>;
export interface NewContractType {
  __typename: 'NewContractType';
  value: string;
}
export type ContractTypeOption = ExistingContractType | NewContractType;

const filterFunc = createFilterOptions<ContractTypeOption>({
  stringify: option => option.value,
  trim: true,
});

export interface ContractTypeAutocompleteProps extends Omit<
  AutocompleteProps<ContractTypeOption, true, false, false>,
  'options' | 'renderInput'
> {
  inputClassName?: string;
  onChangeInput?: (inputValue: string) => void;
  placeholder?: string;
  refetch?: boolean;
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
  suggestNewOption?: boolean;
}

export function ContractTypeAutocomplete({
  blurOnSelect = true,
  inputClassName,
  onChangeInput = doNothing,
  onClose,
  open,
  placeholder = 'Search contract types...',
  refetch,
  renderInput,
  renderOption,
  slotProps,
  suggestNewOption = false,
  value,
  ...rest
}: ContractTypeAutocompleteProps): JSX.Element {
  const callerListboxProps =
    typeof slotProps?.listbox === 'object' && slotProps.listbox !== null ? slotProps.listbox : {};
  const { myContractTypes, refetch: refetchTypes } = useGetMyContractTypes();
  const compareFn = useCallback(
    (a: ExistingContractType, b: ExistingContractType) => a.value.localeCompare(b.value),
    []
  );
  const sortedOptions = useUniqueFilterOptions({ compareFn, items: myContractTypes });

  useAsync(async () => {
    if (refetch) await refetchTypes();
  }, [refetch]);

  return (
    <Autocomplete<ContractTypeOption, true, false, false>
      fullWidth
      multiple
      openOnFocus
      blurOnSelect={blurOnSelect}
      filterOptions={(options, params) => {
        const filteredOptions = filterFunc(options, params);
        if (!suggestNewOption) return filteredOptions;

        const isSuggestingNewOption =
          params.inputValue !== '' &&
          filteredOptions.every(
            ({ value }) => value.toLowerCase() !== params.inputValue.toLowerCase()
          );
        if (!isSuggestingNewOption) return filteredOptions;

        return [
          ...filteredOptions,
          {
            __typename: 'NewContractType',
            value: params.inputValue,
          },
        ];
      }}
      getOptionKey={option =>
        option.__typename === 'ContractType' ? option.id : `new-${option.value}`
      }
      getOptionLabel={option => option.value}
      isOptionEqualToValue={(option, value) => {
        if (getIsExistingContractType(option))
          return getIsExistingContractType(value) && option.id === value.id;

        return option.value === value.value;
      }}
      open={open}
      options={sortedOptions}
      renderInput={params => {
        if (renderInput) return renderInput(params);

        return (
          <DefaultRenderInput
            inputClassName={inputClassName}
            params={params}
            placeholder={placeholder}
          />
        );
      }}
      renderOption={(props, option, state, ownerState) => {
        if (renderOption) return renderOption(props, option, state, ownerState);

        const { key: _key, ...restProps } = props;
        const uniqueKey = getIsExistingContractType(option) ? option.id : `new-${option.value}`;

        return (
          <li key={uniqueKey} {...restProps}>
            <DefaultOption option={option} state={state} />
          </li>
        );
      }}
      renderValue={(types, getItemProps) => {
        return types.map((type, index) => {
          const { key, ...tagProps } = getItemProps({ index });
          return <ContractTypeChip key={key} contractType={type} {...tagProps} />;
        });
      }}
      value={value}
      onClose={onClose}
      onInputChange={(_e, value) => onChangeInput(value)}
      {...rest}
      slotProps={{
        ...slotProps,
        listbox: {
          'data-cy': 'contract-type-autocomplete-options',
          ...callerListboxProps,
        },
      }}
    />
  );
}

interface DefaultRenderInputProps {
  params: AutocompleteRenderInputParams;
  inputClassName?: string;
  placeholder?: string;
}

function DefaultRenderInput({
  inputClassName,
  params,
  placeholder,
}: DefaultRenderInputProps): JSX.Element {
  return (
    <AlineAutocompleteInput
      {...params}
      inputClassName={inputClassName}
      inputProps={{
        ...params.slotProps.htmlInput,
        ['data-cy']: 'contact-type-autocomplete-input',
      }}
      placeholder={placeholder}
    />
  );
}

function getIsExistingContractType(option: ContractTypeOption): option is ExistingContractType {
  return 'id' in option;
}

export const useOptionStyles = makeStyles()(theme => ({
  icon: {
    height: 14,
    width: 14,
  },
  iconCircle: {
    alignItems: 'center',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '50%',
    color: theme.palette.common.white,
    display: 'flex',
    justifyContent: 'center',
    // Use min/max b/c when typing long strings in the autocomplete it preserves height/width and using normal CSS height/width does not.
    maxHeight: 20,
    maxWidth: 20,
    minHeight: 20,
    minWidth: 20,
  },
  optionContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    width: '100%',
  },
}));

function DefaultOption(props: {
  option: ContractTypeOption;
  state: AutocompleteRenderOptionState;
}): JSX.Element {
  const { option } = props;
  const { classes } = useOptionStyles();

  switch (option.__typename) {
    case 'NewContractType':
      return (
        <div className={classes.optionContainer}>
          <div className={classes.iconCircle}>
            <CrossIcon className={classes.icon} />
          </div>
          <Typography noWrap>{option.value}</Typography>
        </div>
      );
    case 'ContractType':
      return <Typography noWrap>{option.value}</Typography>;
  }
}
